import React from 'react'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Table, Portlet, Button, Row, Col, Form } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import EstadosOrdenCompraConstant from 'constants/estadosOrdenCompra'

import * as OrdenCompraService from 'services/ordenCompra.service'

import { IdGen, DownloadAxiosFile } from 'utils'

import * as Modals from './modals'
import PrevisualizarPdfModal from './modals/PrevisualizarPdfModal'

const portletBreadcrumbs = [{ name: 'Ordenes de Compra' }]

const OrdenesCompraLista = ({ history }) => {
  const __zariContext = React.useContext(ZariContext)
  const __adminContext = React.useContext(AdminContext)

  const configUsuario = __adminContext.valoresConfiguracionUsuario || {}
  const esGerenciaPura = !!configUsuario.flagGerencia && !configUsuario.flagLogistica

  const idConsultaRef = React.useRef(null)
  const tablaRef = React.useRef(null)

  const [mostrarDetallePaseKardexModal, setMostrarDetallePaseKardexModal] = React.useState(false)
  const [mostrarPdfModal, setMostrarPdfModal] = React.useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState('')
  const [fechaDesde, setFechaDesde] = React.useState('')
  const [fechaHasta, setFechaHasta] = React.useState('')

  const clickNuevo = React.useCallback(() => {
    history.push(`/${CodigosTransaccionesConstant.ORDENESCOMPRAS_NUEVA}`)
  }, [history])

  const clickEditar = React.useCallback(
    ({ _id }) => {
      history.push(`/${CodigosTransaccionesConstant.ORDENESCOMPRAS_NUEVA}/${_id}`)
    },
    [history]
  )

  const clickImprimir = React.useCallback(
    async ({ _id }) => {
      __zariContext.loader.open('Cargando PDF de orden de compra.')
      await OrdenCompraService.descargarxId(_id)
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          setPdfBlobUrl(url)
          setMostrarPdfModal(true)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
        .finally(() => {
          __zariContext.loader.close()
        })
    },
    [__zariContext]
  )

  const handleClosePdfModal = React.useCallback(() => {
    setMostrarPdfModal(false)
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl('')
    }
  }, [pdfBlobUrl])

  const clickEnviarCorreo = React.useCallback(
    async ({ _id }) => {
      await OrdenCompraService.enviarCorreo(_id)
        .then(() => {
          __zariContext.snack('success', 'El correo ha sido enviado.')
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickAprobar = React.useCallback(
    ({ _id }) => {
      OrdenCompraService.aprobarOrdenxId(_id)
        .then(() => {
          __zariContext.snack('success', 'La orden ha sido aprobada.')
          if (tablaRef.current && tablaRef.current.reload) {
            tablaRef.current.reload()
          }
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickVerHistorialPaseKardex = React.useCallback(({ _id }) => {
    idConsultaRef.current = _id
    setMostrarDetallePaseKardexModal(true)
  }, [])

  const clickDescargarReporte = React.useCallback(() => {
    if (!fechaDesde || !fechaHasta) {
      __zariContext.snack('error', 'Seleccione fecha desde y hasta para el reporte.')
      return
    }
    __zariContext.loader.open('Generando reporte de órdenes de compra.')
    OrdenCompraService.descargarReporteOrdenes({ fechaDesde, fechaHasta })
      .then(({ data }) => {
        const url = window.URL.createObjectURL(
          new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'ReporteOrdenesCompra.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => __zariContext.snack('error', 'Error al generar el reporte.'))
      .finally(() => __zariContext.loader.close())
  }, [__zariContext, fechaDesde, fechaHasta])

  return (
    <>
      <Portlet>
        <Portlet.Header
          title='Ordenes de Compra'
          breadcrumbs={portletBreadcrumbs}
          buttons={esGerenciaPura ? [] : [{ name: 'Nuevo', onClick: clickNuevo }]}
        />
        <Portlet.Body>
          <Row className='mb-3 align-items-end'>
            <Col md={3}>
              <Form.Label text='Desde' />
              <Form.InputDate value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label text='Hasta' />
              <Form.InputDate value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
            </Col>
            <Col md={3}>
              <Button onClick={clickDescargarReporte} tiny outline>
                Descargar Reporte
              </Button>
            </Col>
          </Row>
          <Table.ServerSide
            ref={tablaRef}
            url='/ordenes-compra'
            useNest={true}
            useFilters={true}
            headRows={[
              { label: 'FECHA' },
              { label: 'GLOSA' },
              { label: 'RUC', filter: { type: 'text', name: 'ruc' } },
              { label: 'RAZON SOCIAL', filter: { type: 'text', name: 'razSoc' } },
              { label: 'Numero' },
              { label: 'Usuario' },
              { label: 'ESTADO' },
              { label: '' }
            ]}
            rowTemplate={val => (
              <Table.Row key={IdGen.newId()}>
                <Table.Cell>{val.fechaEmision || '--'}</Table.Cell>
                <Table.Cell>{val.glosa || '--'}</Table.Cell>
                <Table.Cell>{val.ruc || '--'}</Table.Cell>
                <Table.Cell>{val.razonSocial || '--'}</Table.Cell>
                <Table.Cell>
                  {val.serieDocumento && val.numeroDocumento ? val.serieDocumento + '-' + val.numeroDocumento : '--'}
                </Table.Cell>
                <Table.Cell title={val.nombreCompletoCreador || '--'}>{val.usuario || '--'}</Table.Cell>{' '}
                <Table.Cell>
                  {val.estado && EstadosOrdenCompraConstant.__dictionary[val.estado] ? (
                    <span className={`badge badge-${EstadosOrdenCompraConstant.__dictionary[val.estado].color || 'secondary'}`}>
                      {EstadosOrdenCompraConstant.__dictionary[val.estado].nombre}
                    </span>
                  ) : '--'}
                </Table.Cell>
                <Table.Cell>
                  <Table.Dropdown>
                    {({ handleClose }) => [
                      // Aprobar y Editar SOLO se muestran si la OC está en PEND. APROBACIÓN
                      // (estado interno: ABIE = ABIERTA, ahora etiquetada como "PEND. APROBACIÓN").
                      ...(__adminContext.valoresConfiguracionCompania.flagAprobacionOrdenCompra &&
                        val.estado === EstadosOrdenCompraConstant.ABIERTA
                        ? [
                            <Table.Dropdown.Item
                              key='aprobar-orden'
                              handleClose={handleClose}
                              onClick={() => clickAprobar({ _id: val._id })}
                            >
                              Aprobar
                            </Table.Dropdown.Item>
                          ]
                        : []),
                      ...(val.estado === EstadosOrdenCompraConstant.ABIERTA && !esGerenciaPura
                        ? [
                            <Table.Dropdown.Item
                              key='editar'
                              handleClose={handleClose}
                              onClick={() => clickEditar({ _id: val._id })}
                            >
                              Editar
                            </Table.Dropdown.Item>
                          ]
                        : []),
                      <Table.Dropdown.Item
                        key='imprimir-pdf'
                        handleClose={handleClose}
                        onClick={() => clickImprimir({ _id: val._id })}
                      >
                        Imprimir
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='enviar-correo'
                        handleClose={handleClose}
                        onClick={() => clickEnviarCorreo({ _id: val._id })}
                      >
                        Enviar Correo
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='ver-historial'
                        handleClose={handleClose}
                        onClick={() => clickVerHistorialPaseKardex({ _id: val._id })}
                      >
                        Ver Historial Pase Kardex
                      </Table.Dropdown.Item>
                    ]}
                  </Table.Dropdown>
                </Table.Cell>
              </Table.Row>
            )}
          />
        </Portlet.Body>
      </Portlet>
      {mostrarDetallePaseKardexModal && (
        <Modals.DetallePaseKardexModal
          show={mostrarDetallePaseKardexModal}
          handleClose={() => setMostrarDetallePaseKardexModal(false)}
          idOrdenCompra={idConsultaRef.current}
        />
      )}
      {mostrarPdfModal && (
        <PrevisualizarPdfModal show={mostrarPdfModal} handleClose={handleClosePdfModal} pdfBlobUrl={pdfBlobUrl} />
      )}
    </>
  )
}

export default OrdenesCompraLista
